import type { ReactNode } from 'react'

import ShadcnScope from '@/custolink-newdesign/components/ShadcnScope'
import { Button } from '@/custolink-newdesign/ui/button'
import { cn } from '@/custolink-newdesign/lib/utils'

/**
 * Shared full-page layout for the error and not-found pages, new design.
 *
 * Renders standalone, with no AppShell, no AuthShell and no useUiContext: its
 * two callers (custolink-newdesign/pages/ErrorPage.tsx and NotFoundPage.tsx)
 * have to survive a failed ui-context loader as well as a render crash
 * anywhere in the tree, so nothing here may depend on org data or on the
 * admin/asset-frontend shells - only the design tokens, which load globally
 * through app.css regardless of React state. See RouteErrorBoundary.tsx,
 * ErrorBoundary.tsx and the "*" catch-all routes in OrganisationAllRoutes.tsx
 * for where these mount.
 *
 * flow has no styled equivalent for a generic crash screen - its
 * AppErrorBoundary fallback is plain, unstyled markup - but it does share one
 * shadcn layout between its 401 and 404 views (app/(public)/components/
 * ErrorPageView.tsx). This mirrors that idea with this app's own copy and a
 * lucide icon instead of porting flow's two ~200-line SVG illustrations.
 */
export function StatusScreen(p: {
  icon: ReactNode
  /** Tint for the icon's circle. Error states read as alarm; not-found reads
   *  as neutral, so the two pages do not look the same at a glance. */
  tone: 'error' | 'neutral'
  title: string
  description: string
  primaryLabel: string
  secondaryLabel: string
  /**
   * true for the route-level cases (errorElement, the "*" catch-all), which
   * render before anything else has claimed the viewport, so this has to
   * size itself: `min-h-screen`.
   *
   * false for ErrorBoundary.tsx's render-crash fallback, which mounts
   * *inside* AppShell's already viewport-height content column (see the
   * `min-h-svh` chain in AppShell.tsx) - `min-h-screen` there would stack a
   * second viewport's worth of height on top of the toolbar and push the
   * centred message off screen. `flex-1` fills whatever the column already
   * allocated instead.
   */
  fullPage: boolean
  onPrimary: () => void
  onSecondary: () => void
  details?: ReactNode
}) {
  return (
    <ShadcnScope
      className={cn(
        'cl:bg-background cl:flex cl:w-full cl:items-center cl:justify-center cl:px-6 cl:py-12',
        p.fullPage ? 'cl:min-h-screen' : 'cl:min-h-0 cl:flex-1'
      )}
    >
      <div className="cl:motion-safe:animate-in cl:motion-safe:fade-in cl:motion-safe:slide-in-from-bottom-2 cl:w-full cl:max-w-100 cl:text-center cl:duration-500">
        <div
          className={cn(
            'cl:mx-auto cl:grid cl:size-14 cl:flex-none cl:place-items-center cl:rounded-full',
            p.tone === 'error'
              ? 'cl:bg-destructive/10 cl:text-destructive'
              : 'cl:bg-accent cl:text-accent-foreground'
          )}
        >
          {p.icon}
        </div>

        <h1 className="cl:mt-6 cl:text-[27px] cl:leading-tight cl:font-semibold cl:tracking-[-0.02em]">
          {p.title}
        </h1>
        <p className="cl:text-muted-foreground cl:mt-2 cl:text-[14.5px]">{p.description}</p>

        <div className="cl:mt-8 cl:flex cl:items-center cl:justify-center cl:gap-3">
          <Button
            type="button"
            variant="outline"
            aria-label={p.secondaryLabel}
            className="cl:min-h-11 cl:flex-1 cl:rounded-xl cl:text-[14.5px]"
            onClick={p.onSecondary}
          >
            {p.secondaryLabel}
          </Button>
          <Button
            type="button"
            aria-label={p.primaryLabel}
            className="cl:min-h-11 cl:flex-1 cl:rounded-xl cl:text-[14.5px]"
            onClick={p.onPrimary}
          >
            {p.primaryLabel}
          </Button>
        </div>

        {p.details}
      </div>
    </ShadcnScope>
  )
}

export default StatusScreen
